/** Key holding the time (ms) of the last automatic reload after a failed chunk load. */
export const CHUNK_RELOAD_KEY = 'mp-chunk-reload';
/** A second chunk failure within this window is a real outage, not a new deploy: do not loop. */
export const CHUNK_RELOAD_WINDOW_MS = 10_000;

let reloadedInMemory = false;

/**
 * After a deploy an old tab asks for chunk hashes that no longer exist. Returns true when the page
 * should reload now: the first failure, or one more than 10 s after the previous reload. Falls back
 * to an in-memory flag (one reload per page life) when sessionStorage is unavailable.
 */
export function shouldReloadAfterChunkError(
  now: number = Date.now(),
  storage?: Pick<Storage, 'getItem' | 'setItem'>,
): boolean {
  try {
    const store = storage ?? window.sessionStorage;
    const last = Number(store.getItem(CHUNK_RELOAD_KEY));
    if (Number.isFinite(last) && last > 0 && now - last <= CHUNK_RELOAD_WINDOW_MS) return false;
    store.setItem(CHUNK_RELOAD_KEY, String(now));
    return true;
  } catch {
    if (reloadedInMemory) return false;
    reloadedInMemory = true;
    return true;
  }
}

/** Test hook: forget the in-memory fallback flag. */
export function resetChunkReloadMemory(): void {
  reloadedInMemory = false;
}

export function installChunkReloadHandler(): void {
  window.addEventListener('vite:preloadError', (event) => {
    if (!shouldReloadAfterChunkError()) return;
    event.preventDefault();
    window.location.reload();
  });
}

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'error loading dynamically imported module',
  'Element type is invalid',
];

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return CHUNK_ERROR_PATTERNS.some((p) => message.includes(p));
}
